import axios from 'axios';
import { GoogleSheetsResponse, ProductMain, ProductSub, Product, Order, ItemDetail, Item } from '../types';

const API_KEY = 'AIzaSyB2wzsVsN9ek00c36KbORv3qKyvHsx-pYs';
// Default sheet ID - will be replaced with the dynamic one when location is determined
export const DEFAULT_SHEET_ID = '1y0pH-55oYNMUIRCkABMLTQXtRFk-Anlo9uK96bBta8I';
// Mapping sheet ID containing zone data
const MAPPING_SHEET_ID = '1oejRkstRQNzJGPOkuNE7lmxodvbopixrvaCcJaidltU';

// Global variable to store the current sheet ID
let currentSheetId = DEFAULT_SHEET_ID;

// Function to get the base URL with the current sheet ID
const getBaseUrl = () => `https://sheets.googleapis.com/v4/spreadsheets/${currentSheetId}/values`;

// Interface for zone data from the mapping sheet
interface ZoneMapping {
    zone: string;
    sheetUrl: string;
    governorate: string;
    long1: number;
    long2: number;
    long3: number;
    long4: number;
    lat1: number;
    lat2: number;
    lat3: number;
    lat4: number;
}
interface Password {
    password: string;
}
// Function to fetch zone mappings from the mapping sheet
export const fetchZoneMappings = async (): Promise<ZoneMapping[]> => {
    try {
        const response = await axios.get<GoogleSheetsResponse>(
            `https://sheets.googleapis.com/v4/spreadsheets/${MAPPING_SHEET_ID}/values/Sheet1!A2:K?key=${API_KEY}`
        );

        if (!response.data.values) {
            console.error('No zone mapping data found');
            return [];
        }

        return response.data.values.map(row => ({
            zone: row[0] || '',
            sheetUrl: row[1] || '',
            long1: Number(row[2]) || 0,
            long2: Number(row[3]) || 0,
            long3: Number(row[4]) || 0,
            long4: Number(row[5]) || 0,
            lat1: Number(row[6]) || 0,
            lat2: Number(row[7]) || 0,
            lat3: Number(row[8]) || 0,
            lat4: Number(row[9]) || 0,
            governorate: row[10] || ''
        }));
    } catch (error) {
        console.error('Error fetching zone mappings:', error);
        return [];
    }
};
export const fetchPassword = async (): Promise<Password[]> => {
    try {
        const response = await axios.get<GoogleSheetsResponse>(
            `https://sheets.googleapis.com/v4/spreadsheets/${MAPPING_SHEET_ID}/values/Sheet1!m10?key=${API_KEY}`
        );

        if (!response.data.values) {
            console.error('No zone mapping data found');
            return [];
        }

        return response.data.values.map(row => ({
            password: row[0] || ''
        }));
    } catch (error) {
        console.error('Error fetching zone mappings:', error);
        return [];
    }
};
// Function to extract sheet ID from sheet URL
const extractSheetIdFromUrl = (url: string): string => {
    // Try to extract the ID from the URL
    // Typical Google Sheets URL format: https://docs.google.com/spreadsheets/d/SHEET_ID/edit
    const match = url.match(/\/d\/([^/]+)/);
    return match ? match[1] : DEFAULT_SHEET_ID;
};

// Function to check if a point is inside a polygon defined by longitude and latitude boundaries
const isPointInPolygon = (
    userLong: number,
    userLat: number,
    zone: ZoneMapping
): boolean => {
    // تحقق من صحة الإحداثيات
    if (isNaN(userLong) || isNaN(userLat) || 
        !isFinite(userLong) || !isFinite(userLat)) {
        console.error('Invalid coordinates:', userLong, userLat);
        return false;
    }
    
    // تسجيل إضافي لفحص المشكلة
    //console.log(`Checking if point (${userLong}, ${userLat}) is in zone ${zone.zone}`);
    //console.log(`Zone polygon: `, [
    //     { long: zone.long1, lat: zone.lat1 },
    //     { long: zone.long2, lat: zone.lat2 },
    //     { long: zone.long3, lat: zone.lat3 },
    //     { long: zone.long4, lat: zone.lat4 },
    // ]);
    
    // تنفيذ خوارزمية ray-casting لتحديد ما إذا كانت النقطة داخل المضلع
    const polygon = [
        { long: zone.long1, lat: zone.lat1 },
        { long: zone.long2, lat: zone.lat2 },
        { long: zone.long3, lat: zone.lat3 },
        { long: zone.long4, lat: zone.lat4 },
    ];

    let inside = false;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
        const xi = polygon[i].long;
        const yi = polygon[i].lat;
        const xj = polygon[j].long;
        const yj = polygon[j].lat;
        
        // تجنب قسمة صفر أو قيم غير صالحة
        if (isNaN(xi) || isNaN(yi) || isNaN(xj) || isNaN(yj)) {
            continue;
        }

        const intersect = ((yi > userLat) !== (yj > userLat))
            && (userLong < (xj - xi) * (userLat - yi) / (yj - yi) + xi);
        if (intersect) inside = !inside;
    }

    //console.log(`Point is ${inside ? 'inside' : 'outside'} zone ${zone.zone}`);
    return inside;
};

// Function to determine the appropriate sheet ID based on user's location
export const determineSheetIdByLocation = async (userLat: number, userLong: number): Promise<string> => {
    try {
        // تأكد من أن الإحداثيات صالحة
        if (!userLat || !userLong || isNaN(userLat) || isNaN(userLong)) {
            console.error('Invalid coordinates. Using default sheet.');
            return DEFAULT_SHEET_ID;
        }
        
        const zoneMappings = await fetchZoneMappings();
        //console.log(`Fetched ${zoneMappings.length} zones for checking`);
        
        // جرب كل المناطق لتحديد أي منها يحتوي على النقطة
        for (const zone of zoneMappings) {
            if (isPointInPolygon(userLong, userLat, zone)) {
                const sheetId = extractSheetIdFromUrl(zone.sheetUrl);
                //console.log(`Match found! User is in zone: ${zone.zone}, using sheet ID: ${sheetId}`);
                return sheetId;
            }
        }

        //console.log('User not in any defined zone, using default sheet');
        return DEFAULT_SHEET_ID;
    } catch (error) {
        console.error('Error determining sheet ID by location:', error);
        return DEFAULT_SHEET_ID;
    }
};

// تحديث معرف الشيت الحالي
export const updateSheetId = (newSheetId: string): void => {
    currentSheetId = newSheetId;
    // إرسال حدث تحديث الشيت
    window.dispatchEvent(new CustomEvent('sheetIdUpdated', {
        detail: {
            sheetId: newSheetId,
            usingDefault: false
        }
    }));
};

// Function to create a Google Maps link from coordinates
export const createGoogleMapsLink = (latitude: number, longitude: number): string => {
    if (!latitude || !longitude || isNaN(latitude) || isNaN(longitude)) {
        return '';
    }
    return `https://www.google.com/maps?q=${latitude},${longitude}`;
};

// Function to handle location updates while the app is running
export const handleLocationUpdate = async (position: GeolocationPosition): Promise<void> => {
    try {
        // استخراج الإحداثيات من كائن Position
        const { latitude, longitude, accuracy } = position.coords;
        
        // تسجيل مع معلومات إضافية عن الدقة
        //console.log(`Location updated: Lat ${latitude}, Long ${longitude}, Accuracy: ${accuracy}m`);
        //console.log(`Google Maps link: ${createGoogleMapsLink(latitude, longitude)}`);
        
        // تجاهل التحديثات منخفضة الدقة (أكثر من 1000 متر)
        if (accuracy > 1000) {
            //console.log(`Skipping low accuracy location update (${accuracy}m)`);
            return;
        }

        // تأكد من القيم قبل الاستخدام
        if (!latitude || !longitude || isNaN(latitude) || isNaN(longitude)) {
            console.error('Invalid coordinates received:', position.coords);
            return;
        }

        // تحديد ID الورقة بناءً على الموقع الجديد
        const newSheetId = await determineSheetIdByLocation(latitude, longitude);
        updateSheetId(newSheetId);
    } catch (error) {
        console.error('Error handling location update:', error);
    }
};

// Function to initialize the Google Sheets service with user's location
export const initializeGoogleSheetsService = async (): Promise<boolean> => {
    try {
        // Try to get user's location
        if ('geolocation' in navigator) {
            return new Promise((resolve) => {
                // طلب الموقع مع إعدادات محسنة
                const geoOptions = {
                    enableHighAccuracy: true,
                    timeout: 20000,       // زيادة المهلة إلى 20 ثانية
                    maximumAge: 0         // دائمًا الحصول على موقع جديد
                };
                
                navigator.geolocation.getCurrentPosition(
                    async (position) => {
                        // التحقق من دقة الموقع المستلم
                        const { latitude, longitude, accuracy } = position.coords;
                        
                        //console.log(`Initial user location: 
                            // Lat ${latitude}, 
                            // Long ${longitude}, 
                            // Accuracy: ${accuracy}m, 
                            // Timestamp: ${new Date(position.timestamp).toISOString()}`);
                        //console.log(`Google Maps link: ${createGoogleMapsLink(latitude, longitude)}`);
                        
                        // إذا كانت الدقة منخفضة جدًا، قم بتسجيلها ولكن استمر
                        if (accuracy > 1000) {
                            console.warn(`Warning: Low accuracy location (${accuracy}m)`);
                        }

                        // Determine the appropriate sheet ID
                        const sheetId = await determineSheetIdByLocation(latitude, longitude);
                        
                        // Only update if a valid sheet ID was found
                        if (sheetId !== DEFAULT_SHEET_ID) {
                            currentSheetId = sheetId;
                            //console.log(`Using location-specific sheet ID: ${currentSheetId}`);
                        } else {
                            //console.log(`No specific zone found for location, using default: ${DEFAULT_SHEET_ID}`);
                        }

                        // Set up a watch for location changes with improved options
                        const watchId = navigator.geolocation.watchPosition(
                            handleLocationUpdate,
                            (error) => console.error('Error watching location:', error),
                            geoOptions
                        );

                        // Store the watch ID for cleanup
                        (window as any).__locationWatchId = watchId;

                        // Trigger an app-wide refresh
                        window.dispatchEvent(new CustomEvent('sheetIdUpdated', { 
                            detail: { 
                                usingDefault: currentSheetId === DEFAULT_SHEET_ID,
                                latitude,
                                longitude,
                                accuracy,
                                mapsLink: createGoogleMapsLink(latitude, longitude)
                            }
                        }));
                        
                        // Resolve with success
                        resolve(true);
                    },
                    (error) => {
                        console.error('Error getting initial location:', error);
                        console.error('Error code:', error.code, 'Error message:', error.message);
                        
                        // Use default sheet ID if location access is denied
                        //console.log(`Location access denied, using default sheet ID: ${DEFAULT_SHEET_ID}`);
                        
                        // Trigger the event but indicate we're using default
                        window.dispatchEvent(new CustomEvent('sheetIdUpdated', { 
                            detail: { usingDefault: true, error: error.message, errorCode: error.code }
                        }));
                        
                        // Resolve with failure
                        resolve(false);
                    },
                    geoOptions
                );
            });
        } else {
            //console.log('Geolocation not supported by this browser, using default sheet');
            window.dispatchEvent(new CustomEvent('sheetIdUpdated', { 
                detail: { usingDefault: true, error: 'Geolocation not supported' }
            }));
            return false;
        }
    } catch (error) {
        console.error('Error initializing Google Sheets service:', error);
        window.dispatchEvent(new CustomEvent('sheetIdUpdated', { 
            detail: { usingDefault: true, error: 'Service initialization error' }
        }));
        return false;
    }
};

// Function to check if initial data can be fetched
export const checkInitialDataAvailable = async (): Promise<boolean> => {
    try {
        // Try to fetch minimal data to verify the sheet ID is valid
        const response = await axios.get<GoogleSheetsResponse>(
            `${getBaseUrl()}/home!A2:B2?key=${API_KEY}`
        );
        
        return !!response.data.values;
    } catch (error) {
        console.error('Error checking data availability:', error);
        return false;
    }
};

// Function to get current sheet ID status
export const getCurrentSheetStatus = (): { sheetId: string, isDefault: boolean } => {
    return {
        sheetId: currentSheetId,
        isDefault: currentSheetId === DEFAULT_SHEET_ID
    };
};

// Function to clean up location services
export const cleanupLocationServices = (): void => {
    if ('geolocation' in navigator && (window as any).__locationWatchId) {
        navigator.geolocation.clearWatch((window as any).__locationWatchId);
        delete (window as any).__locationWatchId;
        //console.log('Location watch cleared');
    }
};

export const fetchHomeData = async () => {
    try {
        const response = await axios.get<GoogleSheetsResponse>(
            `${getBaseUrl()}/home!A2:K2?key=${API_KEY}`
        );

        if (!response.data.values?.[0]) {
            throw new Error('No data found');
        }

        const row = response.data.values[0];
        return {
            title: row[0],
            carouselImages: row.slice(1, -2).filter(Boolean), // Remove empty values
            whatsappNumber: row[9], // WhatsApp number from J2
            password: row[10], // Password from K2
            notificationPassword: row[10] // Adding notification password for clarity
        };
    } catch (error) {
        console.error('Error fetching home data:', error);
        throw error;
    }
};

export const fetchMainCategories = async () => {
    try {
        const response = await axios.get<GoogleSheetsResponse>(
            `${getBaseUrl()}/Main_Categories!A2:C?key=${API_KEY}`
        );

        if (!response.data.values) {
            return [];
        }

        return response.data.values.map(row => ({
            id: row[0],
            title: row[1],
            iconUrl: row[2]
        }));
    } catch (error) {
        console.error('Error fetching main categories:', error);
        throw error;
    }
};

export const fetchSubCategories = async (mainCategoryId: string) => {
    try {
        const response = await axios.get<GoogleSheetsResponse>(
            `${getBaseUrl()}/Sub_Categories!A2:H?key=${API_KEY}`
        );

        if (!response.data.values) {
            return [];
        }

        return response.data.values
            .filter(row => row[1] === mainCategoryId)
            .map(row => ({
                id: row[0],
                mainCategoryId: row[1],
                title: row[2],
                iconUrl: row[3],
                Meta_Title: row[4] || "Wasal City - وصال سيتي",
                Meta_Description: row[5] || "وصال سيتي, wasal city, خدمات, معلومات, دليل شامل",
                Meta_key_Words: row[6] || "وصال سيتي, wasal city, خدمات, معلومات, دليل شامل",
                url: row[7] || row[2].toLowerCase().replace(/\s+/g, '-')
            }));
    } catch (error) {
        console.error('Error fetching sub categories:', error);
        throw error;
    }
};

export const fetchSubSubCategories = async (subCategoryId: string) => {
    try {
        const response = await axios.get<GoogleSheetsResponse>(
            `${getBaseUrl()}/Sub_Sub_Categories!A2:D?key=${API_KEY}`
        );

        if (!response.data.values) {
            return [];
        }

        return response.data.values
            .filter(row => row[1] === subCategoryId)
            .map(row => ({
                id: row[0],
                subCategoryId: row[1],
                title: row[2],
                iconUrl: row[3]
            }));
    } catch (error) {
        console.error('Error fetching sub-sub categories:', error);
        throw error;
    }
};

export const fetchItems = async (subSubCategoryId: string): Promise<Item[]> => {
    try {
        const response = await axios.get<GoogleSheetsResponse>(
            `${getBaseUrl()}/Items!A2:Z?key=${API_KEY}`
        );

        if (!response.data.values) {
            return [];
        }

        // Log the raw data for debugging
        //console.log('Raw items data:', response.data.values);

        // Map all items first
        const allItems = response.data.values.map(row => ({
            id: row[0],
            subSubCategoryId: row[1],
            title: row[2],
            description: row[3],
            isOpen: row[4] === 'TRUE',
            delivery: row[5] === 'TRUE',
            phone: [row[6], row[7], row[8], row[9]].filter(Boolean).join(','),
            whatsapp: [row[10], row[11], row[12], row[13]].filter(Boolean).join(','),
            location: row[14] || undefined,
            facebook: row[15] || undefined,
            website: row[16] || undefined,
            images: [row[17], row[18], row[19], row[20]].filter(Boolean),
            Meta_Title: row[21] || "Wasal City - وصال سيتي",
            Meta_Description: row[22] || "وصال سيتي, wasal city, خدمات, معلومات, دليل شامل",
            Meta_key_Words: row[23] || "وصال سيتي, wasal city, خدمات, معلومات, دليل شامل",
            url: row[24] || row[2].toLowerCase().replace(/\s+/g, '-'),
            notificationPassword: row[25] // Adding notification password from column Z
        }));

        // Filter by subSubCategoryId if it's not "all"
        let items = allItems;
        if (subSubCategoryId && subSubCategoryId !== "all") {
            items = allItems.filter(item => item.subSubCategoryId === subSubCategoryId);
        }

        // Log the processed items for debugging
        //console.log('Filtered items for subcategory:', subSubCategoryId, items);

        return items;
    } catch (error) {
        console.error('Error fetching items:', error);
        throw error;
    }
};

export const fetchProductMains = async (itemId: string): Promise<ProductMain[]> => {
    try {
        const response = await axios.get<GoogleSheetsResponse>(
            `${getBaseUrl()}/Product_Main!A2:D?key=${API_KEY}`
        );

        if (!response.data.values) {
            return [];
        }

        return response.data.values
            .filter(row => row[1] === itemId)
            .map(row => ({
                id: row[0],
                itemId: row[1],
                title: row[2],
                iconUrl: row[3]
            }));
    } catch (error) {
        console.error('Error fetching product mains:', error);
        return [];
    }
};

export const fetchProductSubs = async (productMainId: string): Promise<ProductSub[]> => {
    try {
        const response = await axios.get<GoogleSheetsResponse>(
            `${getBaseUrl()}/Product_Sub!A2:H?key=${API_KEY}`
        );
        return response.data.values?.filter(row => row[1] === productMainId)
            .map(row => ({
                id: row[0],
                productMainId: row[1],
                title: row[2],
                iconUrl: row[3],
                Meta_Title: row[4] || "Wasal City - وصال سيتي",
                Meta_Description: row[5] || "وصال سيتي, wasal city, خدمات, معلومات, دليل شامل",
                Meta_key_Words: row[6] || "وصال سيتي, wasal city, خدمات, معلومات, دليل شامل",
                url: row[7] || row[2].toLowerCase().replace(/\s+/g, '-')
            })) || [];
    } catch (error) {
        console.error('Error fetching product subs:', error);
        return [];
    }
};

export const fetchProducts = async (productSubId: string): Promise<Product[]> => {
    try {
        const response = await axios.get<GoogleSheetsResponse>(
            `${getBaseUrl()}/Product!A2:F?key=${API_KEY}`
        );
        return response.data.values?.filter(row => row[1] === productSubId)
            .map(row => ({
                id: row[0],
                productSubId: row[1],
                title: row[2],
                imageUrl: row[3],
                salePrice: row[4] ? Number(row[4]) : null,
                mainPrice: Number(row[5])
            })) || [];
    } catch (error) {
        console.error('Error fetching products:', error);
        return [];
    }
};

export const submitOrder = async (order: Order): Promise<void> => {
    try {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        const egyptianDate = new Date(order.date).toLocaleDateString('ar-EG', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
        });

        const formattedItems = order.productItems.map((item, index) =>
            `${index + 1}. ${item.title}\n` +
            `   الكمية: ${item.quantity}\n` +
            `   السعر: ${item.price} جنيه\n` +
            `   الإجمالي: ${item.total} جنيه`
        ).join('\n\n');

        const orderData = {
            items: formattedItems,
            totalPrice: order.totalPrice,
            date: egyptianDate,
            whatsapp: user.phone
        };

        const SCRIPT_URL = 'https://script.google.com/macros/s/AKfycbxW2VcUi90PSa4d1998n05jR5HJlz4FeFng9dCV4Rt3DjEqkyNeP92fEsStwqe5MHBs/exec';

        const response = await fetch(SCRIPT_URL, {
            method: 'POST',
            mode: 'no-cors',
            body: JSON.stringify(orderData)
        });

        const whatsappMessage = formatWhatsAppMessage(order, egyptianDate);
        const formattedNumber = order.whatsappNumber;
        window.open(
            `https://wa.me/${formattedNumber}?text=${encodeURIComponent(whatsappMessage)}`,
            '_blank'
        );

    } catch (error) {
        console.error('Error submitting order:', error);
        throw new Error('فشل في تسجيل الطلب. يرجى المحاولة مرة أخرى.');
    }
};

function formatWhatsAppMessage(order: Order, egyptianDate: string): string {
    const itemsList = order.productItems.map((item, index) =>
        `${index + 1}. ${item.title}\n` +
        `   الكمية: ${item.quantity}\n` +
        `   السعر: ${item.price} جنيه\n` +
        `   الإجمالي: ${item.total} جنيه`
    ).join('\n\n');

    return `طلب جديد\n\n${itemsList}\n\n` +
        `المجموع الكلي: ${order.totalPrice} جنيه\n\n` +
        `تاريخ الطلب: ${egyptianDate}`;
}

export const fetchItemDetails = async (itemId: string): Promise<ItemDetail> => {
    try {
        //console.log('Fetching item details for ID:', itemId);
        const response = await axios.get<GoogleSheetsResponse>(
            `${getBaseUrl()}/Items!A2:X?key=${API_KEY}`
        );

        if (!response.data.values) {
            throw new Error('No data found');
        }

        //console.log('All items:', response.data.values); // للتأكد من البيانات

        const item = response.data.values.find(row => row[0] === itemId);

        if (!item) {
            throw new Error(`Item not found with ID: ${itemId}`);
        }

        //console.log('Found item:', item); // للتأكد من العثور على العنصر

        return {
            id: item[0],
            title: item[2],
            description: item[3],
            isOpen: item[4] === 'TRUE',
            delivery: item[5] === 'TRUE',
            phone: [item[6], item[7], item[8], item[9]].filter(Boolean).join(','),
            whatsapp: [item[10], item[11], item[12], item[13]].filter(Boolean).join(','),
            location: item[14] || undefined,
            facebook: item[15] || undefined,
            website: item[16] || undefined,
            images: [item[17], item[18], item[19], item[20]].filter(Boolean),
            Meta_Title: item[21] || "Wasal City - وصال سيتي",
            Meta_Description: item[22] || "وصال سيتي, wasal city, خدمات, معلومات, دليل شامل",
            Meta_key_Words: item[23] || "وصال سيتي, wasal city, خدمات, معلومات, دليل شامل"
        };
    } catch (error) {
        console.error('Error in fetchItemDetails:', error);
        throw error;
    }
};